import * as tslib_1 from "tslib";
/**
 * @file Component with standard form functionality.
 * @author Tom Verschoore de la Houssaije.
 */
import { EventEmitter, OnDestroy } from '@angular/core';
import { DIRTY_FLAG } from '../../core/breeze.service';
import { ControlBase } from '../../base/helpers/control-base';
import { FormControl, FormGroup } from '@angular/forms';
import { EntityState, EntityAction } from 'breeze-client';
import { DataRights } from '../../core/helpers/data-rights.enum';
import { RoutesService } from '../../routes.service';
/**
 * Shows one selected entity in a form for editing.
 */
var FormBase = /** @class */ (function (_super) {
    tslib_1.__extends(FormBase, _super);
    /**
     * Constructs a FormComponent instance.
     * @param configurationsService The configuration service.
     * @param breezeService The domain service
     * @param route The activated route.
     * @param translateService The translation service.
     * @param alertsService The alerts service.
     */
    function FormBase(breezeService, route, router, translateService, alertsService, resourceService) {
        var _this = _super.call(this, alertsService) || this;
        _this.breezeService = breezeService;
        _this.route = route;
        _this.router = router;
        _this.translateService = translateService;
        _this.alertsService = alertsService;
        _this.resourceService = resourceService;
        /**
         * Gets, sets the flag indicating whether or not the form is shown in the overlay.
         */
        _this.overlayMode = false;
        /** Event that emits when an entity is saved */
        _this.itemSaved = new EventEmitter();
        /** Event that emits when an entity is removed */
        _this.itemRemoved = new EventEmitter();
        /** Event that emits when changes are canceled. */
        _this.itemCanceled = new EventEmitter();
        _this.hasCloseButton = true;
        _this.useCache = true;
        /** Represents the edit mode of the form.
         * The edit mode of the form is one of the following values: "unknown", "edit" or "new".
         */
        _this.formMode = 'unknown';
        /** The loading state */
        _this.isLoading = true;
        /** A list of validation errors */
        _this.validationErrors = [];
        /** Holds an Angular ControlGroup. We use this to support user feedback on validation errors. */
        _this.form = null;
        /** The key value of the depicted entity. */
        _this.entityKeyValue = undefined;
        /**
         * Field to hold the virtual key value of an entity.
         * @protected
         * @type {string[]}
         */
        _this.virtualKeyValue = undefined;
        /** Holds the depicted entity. */
        _this._item = null;
        return _this;
    }
    Object.defineProperty(FormBase.prototype, "labelKey", {
        /**
         * Returns the configuration label key unless overridden.
         *
         * @type {string}
         */
        get: function () {
            if (this._labelKey) {
                return this._labelKey;
            }
            return this.configuration.labelKey;
        },
        /**
         * Sets a custom label that overrides the configuration label.
         */
        set: function (label) {
            this._labelKey = label;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBase.prototype, "editMode", {
        /** Gets a boolean value indicating wether the form is in "edit" mode. */
        get: function () {
            if (!this.item) {
                return false;
            }
            return !this.createMode;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBase.prototype, "createMode", {
        /** Gets a boolean value indicating wether the form is in "new" mode. */
        get: function () {
            if (!this.item) {
                return false;
            }
            var entityState = this.item.entityAspect.entityState;
            return entityState === EntityState.Detached || entityState === EntityState.Added;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBase.prototype, "hasChanges", {
        /**
         * Checks whether or not the displayed entity has changed.
         * @readonly
         * @type {boolean}
         */
        get: function () {
            if (!this.item) {
                return false;
            }
            var entityState = this.item.entityAspect.entityState;
            return entityState === EntityState.Modified;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBase.prototype, "item", {
        /** Gets the depicted entity. */
        get: function () {
            return this._item;
        },
        /** Sets the depicted entity. */
        set: function (value) {
            var _this = this;
            if (this._item === value) {
                return;
            }
            this._item = value;
            if (!this._item) {
                this.entityKeyValue = null;
                return;
            }
            // this.validationErrors.splice(0, this.validationErrors.length);
            this.validationErrors.length = 0;
            if (this.form && this.form.controls) {
                Object.keys(this.form.controls).forEach(function (key) {
                    var control = _this.form.controls[key];
                    // DO NOT USE control.reset();!!!!!
                    control.markAsUntouched();
                    control.markAsPristine();
                });
            }
            if (this.entitySubscription && this._item) {
                this._item.entityAspect.entityManager.entityChanged.unsubscribe(this.entitySubscription);
            }
            if (this.propertySubscription && this._item) {
                this._item.entityAspect.propertyChanged.unsubscribe(this.propertySubscription);
            }
            this.entityKeyValue = this._item.entityAspect.getKey().values;
            this.formMode = 'edit';
            if (this._item.entityAspect.entityState.isAdded()) {
                this.formMode = 'new';
                this.setDefaultValues();
            }
            this.entitySubscription = this._item.entityAspect.entityManager.entityChanged.subscribe(function (args) {
                if (_this._item === args.entity
                    && args.entityAction === EntityAction.EntityStateChange
                    && _this._item.entityAspect.entityState === EntityState.Deleted) {
                    _this._item.entityAspect.entityManager.entityChanged.unsubscribe(_this.entitySubscription);
                    var saveResult_1 = {
                        entities: [args.entity],
                        keyMappings: null,
                        XHR: null
                    };
                    window.setTimeout(function () {
                        var working = _this.breezeService.savingInProgress;
                        if (working) {
                            _this.subscriptionPool.add(working.subscribe({
                                complete: function () {
                                    _this.itemRemoved.emit(saveResult_1);
                                }
                            }));
                            return;
                        }
                        _this.itemRemoved.emit(saveResult_1);
                    });
                }
            });
            this.propertySubscription = this._item.entityAspect.propertyChanged.subscribe(function (args) {
                if (_this._item.entityAspect.entityState.isDeleted()) {
                    return;
                }
                if (args.propertyName) {
                    _this._item[DIRTY_FLAG] = true;
                    if (!_this.form) {
                        return;
                    }
                    // Needed for updating state PeriodValidator.
                    switch (args.propertyName) {
                        case 'Start':
                            var finishControl = _this.form.get('Finish');
                            if (finishControl) {
                                finishControl.updateValueAndValidity();
                            }
                            break;
                        case 'Finish':
                            var startControl = _this.form.get('Start');
                            if (startControl) {
                                startControl.updateValueAndValidity();
                            }
                            break;
                    }
                }
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBase.prototype, "isReadOnly", {
        /** Gets whether or not the form is readonly. */
        get: function () {
            return this.getIsReadOnly();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBase.prototype, "entityKey", {
        /**
         * Gets the key of entity shown.
         * @readonly
         * @type {*}
         */
        get: function () {
            var result = '';
            if (this._item) {
                var values = this._item.entityAspect.getKey().values;
                values.forEach(function (value) {
                    result += '_' + value;
                });
                return result.substring(1);
            }
            if (this.entityKeyValue == null) {
                return null;
            }
            if (this.entityKeyValue instanceof Array) {
                this.entityKeyValue.forEach(function (value) {
                    result += '_' + value;
                });
                return result.substring(1);
            }
            return this.entityKeyValue;
        },
        /**
         * Sets the key of the entity shown.
         */
        set: function (value) {
            if (this.entityKeyValue === value) {
                return;
            }
            this.isLoading = true;
            if (value == null) {
                this.entityKeyValue = null;
                this.virtualKeyValue = undefined;
                this.refresh();
                return;
            }
            if (typeof value === 'string') {
                if (value.indexOf('K_-') !== 0) {
                    // Is not a temporary new key
                    // Can by a multi key.
                    this.entityKeyValue = value.split('_');
                    if (this.entityKeyValue.length > 1) {
                        this.virtualKeyValue = this.entityKeyValue;
                    }
                    else if (this.virtualKeyValue && this.virtualKeyValue.indexOf(this.entityKeyValue[0]) === -1) {
                        this.virtualKeyValue = undefined;
                    }
                }
                else {
                    this.entityKeyValue = value;
                    this.virtualKeyValue = undefined;
                }
            }
            else if (value instanceof Array) {
                this.entityKeyValue = value;
                if (this.entityKeyValue.length > 1) {
                    this.virtualKeyValue = this.entityKeyValue;
                }
                else if (this.virtualKeyValue && this.virtualKeyValue.indexOf(this.entityKeyValue[0]) === -1) {
                    this.virtualKeyValue = undefined;
                }
            }
            else {
                // Wat voor type is dit dan?
                this.entityKeyValue = value;
                this.virtualKeyValue = undefined;
            }
            this.refresh();
        },
        enumerable: true,
        configurable: true
    });
    FormBase.prototype.publishErrors = function (errors, message) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var promise;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (errors instanceof Array) {
                            this.validationErrors = errors;
                            return [2 /*return*/];
                        }
                        if (!(errors.entityErrors && errors.entityErrors.length > 0)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.breezeService.publishServerEntityErrors(errors.entityErrors, this.configuration.data)];
                    case 1:
                        promise = _a.sent();
                        this.validationErrors = promise;
                        return [2 /*return*/];
                    case 2:
                        this.alertsService.createAlert('warning', message, this.breezeService.entityErrorInfo(errors.entityErrors));
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Is called when a registered control is deactivated.
     * @see RegisteredControl.onControlDeactivate
     * @param configuration The reference of the configuration.
     */
    FormBase.prototype.onControlDeactivate = function (configuration) {
        // TODO: Consider and test wether we can remove this functionality. Use ngDestroy instead?
        if (this._item && this._item.entityAspect.entityState === EntityState.Modified) {
            this.cancelItem();
        }
    };
    /**
     * We cancel pending changes on destroy
     */
    FormBase.prototype.ngOnDestroy = function () {
        var _this = this;
        _super.prototype.ngOnDestroy.call(this);
        // TODO: Consider message, Save this?, for user
        // TODO: Consider CanDeactivate guard instead of ngOnDestroy (On AbstractView?)
        if (this._item) {
            if (this._item.entityAspect.entityManager && this.entitySubscription) {
                this._item.entityAspect.entityManager.entityChanged.unsubscribe(this.entitySubscription);
            }
            // Eerst alleen check op isModified, maar vanwege issue #2897 nu ook op isAdded.
            if (this._item.entityAspect.entityState.isAdded() || this._item.entityAspect.entityState.isModified()) {
                window.setTimeout(function () {
                    _this.breezeService.rejectChanges();
                });
            }
            if (this.propertySubscription) {
                this._item.entityAspect.propertyChanged.unsubscribe(this.propertySubscription);
            }
        }
    };
    /**
     * Checks wether the entity has required fields.
     * @returns True when a required field is found, otherwise false.
     */
    FormBase.prototype.hasRequiredFields = function () {
        for (var _i = 0, _a = this.configuration.data.fields; _i < _a.length; _i++) {
            var field = _a[_i];
            if (field.required) {
                return true;
            }
        }
        return false;
    };
    /**
     * Retrieves the entity of the type FormComponent.entityType with the key value FormComponent.entityKeyValue so it can be shown.
     */
    FormBase.prototype.refresh = function () {
        if (!this.entityKeyValue) {
            this.item = null;
            return;
        }
        this.getEntityWith(this.entityKeyValue);
    };
    /**
     * Save all the made changes. This method is used in the "edit" mode.
     * @param event The HTML event argument.
     * @param forceSave Save changes even when there are none according to the breeze service.
     */
    FormBase.prototype.saveItem = function (event, forceSave) {
        var _this = this;
        if (forceSave === void 0) { forceSave = false; }
        var entityState = this._item ? this._item.entityAspect.entityState : null;
        var newOrDeleted = entityState ? entityState.isAdded() || entityState.isDeleted() : false;
        if (!forceSave && !this.breezeService.hasChanges() && !this.breezeService.hasAdded()) {
            this.doneSaving(newOrDeleted);
            return;
        }
        this.validationErrors.length = 0;
        if (event && !event.target.classList.contains('sym-working')) {
            event.target.classList.add('sym-working');
        }
        this.subscriptionPool.add(this.breezeService.saveChanges(function (error) {
            // Remove sym-working to show that action is finished.
            if (event) {
                event.target.classList.remove('sym-working');
            }
            // Give correct alert based on whether an error is given or not.
            if (error) {
                if (entityState && entityState.isAdded()) {
                    // If added then required can by invalid without being touched and dirty.
                    var entityErrors = _this._item.entityAspect.getValidationErrors();
                    if (entityErrors) {
                        entityErrors.forEach(function (err) {
                            if (err.validator && err.validator.name === 'required') {
                                var cntrl = _this.form.controls[err.propertyName];
                                if (cntrl) {
                                    // Werkte niet vanwege => changeDetection: ChangeDetectionStrategy.OnPush
                                    // Dit is nu overal weggehaald.
                                    cntrl.markAsTouched();
                                    cntrl.markAsDirty();
                                }
                            }
                        });
                    }
                }
                _this.publishErrors(error, 'APP.FORMS.ITEMS_UNSUCCESSFULLY_SAVED');
            }
            else {
                _this.alertsService.createAlert('success', 'APP.FORMS.ITEMS_SUCCESSFULLY_SAVED');
            }
        }, this.configuration.data).subscribe(function (value) {
            _this.itemSaved.emit(value);
            _this.doneSaving(newOrDeleted);
        }));
    };
    /**
     * Save all the made changes. This method is used in the "new" mode.
     * @param event The HTML event argument.
     */
    FormBase.prototype.createItem = function (event) {
        this.saveItem(event, true);
    };
    /**
     * Closes the detail right side of the application.
    * */
    FormBase.prototype.close = function () {
        this.navigateToClose();
        // Notify parent component.
        this.itemCanceled.emit(this.item);
    };
    /**
     * Cancels all the made changes and closes the sidebar.
     */
    FormBase.prototype.cancelItem = function () {
        if (this.item) {
            // RejectChanges on item level is not enough.
            // Navigation properties can also change.
            this.breezeService.rejectChanges();
            this.itemCanceled.emit(this.item);
        }
        this.navigateToClose();
    };
    /**
     * Removes the depicted entity item.
     * @param event The HTML event argument.
     */
    FormBase.prototype.removeItem = function (event) {
        var _this = this;
        if (!this.item) {
            return;
        }
        if (this.item.entityAspect.entityState.isAdded()) {
            this.cancelItem();
            return;
        }
        this.breezeService.setDeleted(this.item);
        this.item.entityAspect.entityManager.entityChanged.unsubscribe(this.entitySubscription);
        this.item = null;
        if (event) {
            event.target.classList.add('sym-working');
        }
        this.subscriptionPool.add(this.breezeService.saveChanges(function (error) {
            // Remove sym-working to show that action is finished
            if (event) {
                event.target.classList.remove('sym-working');
            }
            // Give correct alert based on whether an error is given or not
            if (error) {
                _this.publishErrors(error, 'APP.FORMS.ITEMS_UNSUCCESSFULLY_DELETED');
            }
            else {
                _this.alertsService.createAlert('success', 'APP.FORMS.ITEMS_SUCCESSFULLY_DELETED');
            }
        }).subscribe(function (value) {
            _this.itemRemoved.emit(value);
        }));
    };
    /**
     * Is called after the block name is set and the configuration is loaded.
     */
    FormBase.prototype.afterInitialize = function () {
        _super.prototype.afterInitialize.call(this);
        this.setUpForm();
    };
    /**
     * Is called after saving.
     */
    FormBase.prototype.doneSaving = function (newOrDeleted) {
        if (newOrDeleted) {
            // If new or deleted then closing navigate will be done by
            // DetailComponent.navigateBackFromDeleted
            // DetailComponent.navigateBackFromNew
            // Avoiding override DetailComponent navigation.
            // TODO: Consider refactoring.
            return;
        }
        this.navigateToClose();
    };
    /**
     * Restores to url with only main view without right details.
     * @protected
     */
    FormBase.prototype.navigateToClose = function () {
        if (this.overlayMode) {
            return;
        }
        RoutesService.navigateBack(this.router, this.route, this.route.snapshot.queryParams);
    };
    /**
     * Retrieves the entity, of the type FormComponent.entityType,
     * with the requested key add assigns the entity to the FormComponent.item property.
     * @param key The key of the requested entity.
     */
    FormBase.prototype.getEntityWith = function (key) {
        if (!this.entityType) {
            return;
        }
        this.tryGetItem(key);
    };
    /**
     * Gets whether or not the form is readonly.
     * Is used for the isReadOnly and to override properly.
     * Typescript super key word does not give access for properties.
     * @protected
     * @returns {boolean} Value whether or not the form is readonly.
     * @memberOf FormBase
     */
    FormBase.prototype.getIsReadOnly = function () {
        if (this.canCreate || this.canUpdate) {
            return false;
        }
        return true;
    };
    /**
     * Tries to get the entity with the specified key.
     * @protected
     * @param {string[]} The key of the entity to find.
     */
    FormBase.prototype.tryGetItem = function (key) {
        var _this = this;
        var dataConfig = this.configuration.data;
        this.subscriptionPool.add(this.breezeService.getEntityByKey(this.entityType, key, this.useCache, dataConfig.expand).subscribe({
            next: function (value) {
                _this.isLoading = false;
                _this.item = value;
                if (_this.item == null) {
                    // Fix issue: #2543
                    _this.rights = DataRights.Read;
                }
                else {
                    // Is needed to restore rights from setting rights to DataRights.Read.
                    _this.rights = _this._configuration.rights;
                }
            },
            error: function (reason) {
                _this.isLoading = false;
                _this.item = null;
                // Fix issue: #2543
                _this.rights = DataRights.Read;
            }
        }));
    };
    /**
     * Contructs an Angular FormGroup with the requested configuration
     * and assigns it to the FormComponent.form property.
     */
    FormBase.prototype.setUpForm = function () {
        var _this = this;
        if (!this.configuration.data) {
            return;
        }
        // Issue: Je kan niet zien of de aanwezigheid goed is aangemaakt #2786
        var fields = [];
        this.configuration.data.fields.forEach(function (field) {
            var obj = {
                get readonly() {
                    // Gebruik hier self niet this.isReadOnly!!!
                    return this.isReadOnly ? true : field.readonly;
                },
                set readonly(value) {
                    field.readonly = value;
                }
            };
            Object.keys(field).forEach(function (key) {
                if (key === 'readonly') {
                    return;
                }
                obj[key] = field[key];
            });
            fields.push(obj);
        });
        this.configuration.data.fields = fields;
        if (this.formMode === 'new') {
            this.setDefaultValues();
        }
        var entity = this.breezeService.getEntityType(this.configuration.data.entity);
        var group = new FormGroup({});
        this.entityType = entity;
        this.configuration.data.fields.forEach(function (field) {
            ControlBase.setFieldDefaults(field, _this.entityType);
            var control = new FormControl();
            control.setValidators(function (c) {
                return _this.getControlErrors(field.name);
            });
            group.addControl(field.name, control);
        });
        this.form = group;
        this.refresh();
    };
    /**
     * Sets default values in case any are defined in the configuration
     * @private
     * @memberOf FormBase
     */
    FormBase.prototype.setDefaultValues = function () {
        var _this = this;
        if (!this.item || !this.configuration || !this.configuration.data || !this.configuration.data.fields) {
            return;
        }
        this.configuration.data.fields.forEach(function (field) {
            if (field.default !== undefined) {
                if (field.default === '@resource') {
                    _this.item[field.name + '_Oid'] = _this.resourceService.getResourceOid();
                }
                else {
                    _this.item[field.name] = field.default;
                }
            }
        });
    };
    /**
     * Gets the control errors for a property.
     * @param property The name of the property.
     * @returns The property errors otherwise null;
     */
    FormBase.prototype.getControlErrors = function (property) {
        if (!this.item) {
            return null;
        }
        var errors = this.item.entityAspect.getValidationErrors(property);
        if (errors.length === 0) {
            return null;
        }
        var controlErrors = {};
        this.subscriptionPool.add(this.translateService.get(errors.map(function (error) { return error.key.split(':')[0]; })).subscribe(function (values) {
            errors.forEach(function (error) {
                var label = error.key.split(':')[0];
                if (values[label] === label) {
                    controlErrors[error.key] = error.errorMessage ? error.errorMessage : label;
                }
                else {
                    controlErrors[error.key] = values[label];
                }
            });
        }));
        return controlErrors;
    };
    return FormBase;
}(ControlBase));
export { FormBase };
